import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { isOnlineMode } from "lib/consts/webapp";

import { FleetRepository } from "lib/fleet/FleetRepository";

import merge from "lodash/merge";
import unionWith from "lodash/unionWith";
import { ARCInfo } from "arcjs/common/arc_devices";
import { ARCStatus, Fleet } from "types/fleet";
import { AnyPlainObject } from "arcjs/common/misc";

type ARCStatuses = { [serial: string]: Partial<ARCStatus> };

export type ArcsContextType = {
    fleetRepo: FleetRepository;
    arcsInfo: ARCInfo[];
    fleets: Fleet[];
    getFleetById: (id: string) => Fleet;
    arcStatuses: ARCStatuses;
};

export const ArcsContext = createContext<ArcsContextType>(null!);
ArcsContext.displayName = "arcs";

export const useFleet = () => useContext(ArcsContext);

const STATUS_FETCH_INTERVAL_S = 10;

const mergeAsNewObj = (object: AnyPlainObject, ...others: AnyPlainObject[]) =>
    merge({}, object, ...others);

const useArcStatuses = ({ fleetRepo, arcsInfo }) => {
    const [arcStatuses, setARCStatuses] = useState({});
    const updateARCStatuses = useCallback(async () => {
        const newStatuses = {};
        for (const { serialNumber } of arcsInfo) {
            try {
                const status = await fleetRepo.getARCStatus(serialNumber);
                newStatuses[serialNumber] = { timestamp: Date.now(), ...status };
            } catch { }
        }
        setARCStatuses((currStatuses) => mergeAsNewObj({}, currStatuses, newStatuses));
    }, [fleetRepo, arcsInfo]);

    useEffect(() => {
        updateARCStatuses();
        const intervalId = setInterval(updateARCStatuses, STATUS_FETCH_INTERVAL_S * 1000);
        return () => {
            clearInterval(intervalId);
        };
    }, [updateARCStatuses]);

    return arcStatuses;
};

const FleetProvider = ({ children }) => {
    const [arcsInfo, setArcs] = useState([]);
    const [fleets, setFleets] = useState([]);

    const fleetRepo = useMemo(() => new FleetRepository({ useDummy: !isOnlineMode }), []);

    const arcStatuses = useArcStatuses({ fleetRepo, arcsInfo });

    const getFleetById = useCallback(
        (id: string) => {
            return fleets.find((f) => f.id === id);
        },
        [fleets]
    );

    useEffect(() => {
        fleetRepo.getAll().then((fleets) => {
            setFleets(fleets);
        });
        fleetRepo.getAllArcs().then((arcsInfo) => {
            console.log("arcsInfo:", arcsInfo);
            setArcs(
                arcsInfo.map((info) => ({ ...info, controlSource: info.controlSource || "DUMMY" }))
            );
        });
    }, [setArcs, fleetRepo, setFleets]);

    useEffect(() => {
        fleetRepo.subscribeChanges((arc) => {
            setArcs((currArcs) =>
                unionWith([arc], currArcs, (a, b) => a.serialNumber === b.serialNumber)
            );
        });
    }, [fleetRepo]);

    return (
        <ArcsContext.Provider value={{ fleetRepo, arcsInfo, fleets, getFleetById, arcStatuses }}>
            {children}
        </ArcsContext.Provider>
    );
};

export default FleetProvider;
